import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Shown over the board when the server refuses a join.
 *
 * It lives on the Game route rather than the menu because people arrive at
 * /game/:id from the Share Room Link button too — a prompt on the menu would
 * need a second implementation for the link path, and the two would drift.
 *
 * A sibling of KickedNotice, and deliberately the same shape: being stopped at
 * the door without a word reads as a bug.
 */
const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'rgba(14,8,4,0.82)',
  },
  plate: {
    background: '#2a1d13',
    border: '1px solid #6b4622',
    borderRadius: '12px',
    padding: '26px 30px',
    maxWidth: '340px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    textAlign: 'center',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.14)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 18px 50px rgba(0,0,0,0.55)',
    ].join(','),
  },
  label: {
    fontFamily: MONO,
    color: '#e35c36',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '3px',
    textTransform: 'uppercase',
  },
  message: { color: '#f5e6c8', fontSize: '17px', lineHeight: 1.45 },
  hint: { color: '#c4a070', fontSize: '13px', lineHeight: 1.45 },
  error: { color: '#e35c36', fontSize: '13px', fontWeight: 700, lineHeight: 1.45 },
  form: { display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', marginTop: '4px' },
  // Recessed, per the panel system: inputs show focus by shifting borderColor,
  // never an outline ring.
  input: {
    width: '100%',
    boxSizing: 'border-box',
    height: '46px',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    color: '#f5e6c8',
    padding: '0 14px',
    fontSize: '15px',
    fontFamily: MONO,
    outline: 'none',
    textAlign: 'center',
    letterSpacing: '1px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color 0.15s',
    '&:focus': { borderColor: '#8b4513' },
    '&::placeholder': { color: '#6a4a2a', letterSpacing: 0 },
  },
  btn: {
    height: '46px',
    padding: '0 22px',
    background: '#d94f2b',
    border: 'none',
    borderRadius: '8px',
    color: '#fff3e8',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.22)',
      'inset 0 -2px 0 rgba(0,0,0,0.28)',
      '0 3px 0 #7d2a12',
    ].join(','),
    '&:active': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 2px 5px rgba(0,0,0,0.45)',
    },
  },
  ghostBtn: {
    background: 'none',
    border: '1px solid #6b4622',
    borderRadius: '8px',
    color: '#d99a6a',
    height: '40px',
    padding: '0 16px',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    '&:hover': { borderColor: '#d94f2b', color: '#fff3e6' },
  },
});

function RoomGate({ reason, retry = false, onSubmit, onLeave }) {
  const classes = useStyles();
  const t = useT();
  const [password, setPassword] = useState('');

  // The door, not the lock. "Closed" reads as "this game has ended", so the
  // copy says what is actually true.
  if (reason !== 'password') {
    return (
      <div className={classes.overlay} role="dialog" aria-modal="true">
        <div className={classes.plate}>
          <span className={classes.label}>{t('access.gateNoEntryTitle')}</span>
          <span className={classes.message}>{t('access.refusedNotAccepting')}</span>
          <span className={classes.hint}>{t('access.hostCanReopen')}</span>
          <button type="button" className={classes.btn} onClick={onLeave}>{t('game.backToMenu')}</button>
        </div>
      </div>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    const value = password.trim();
    if (!value) return;
    onSubmit(value);
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true">
      <div className={classes.plate}>
        <span className={classes.label}>{t('access.gateLockedTitle')}</span>
        <span className={classes.message}>{t('access.needsPassword')}</span>
        {retry
          ? <span className={classes.error}>{t('access.wrongPassword')}</span>
          : <span className={classes.hint}>{t('access.askTheHost')}</span>}

        <form className={classes.form} onSubmit={submit}>
          <input
            className={classes.input}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('access.passwordPlaceholderShort')}
            aria-label={t('a11y.roomPassword')}
            autoComplete="off"
            autoFocus
          />
          <button type="submit" className={classes.btn}>{t('access.enterRoom')}</button>
        </form>

        <button type="button" className={classes.ghostBtn} onClick={onLeave}>{t('game.backToMenu')}</button>
      </div>
    </div>
  );
}

export default RoomGate;
